import { useCallback, useEffect, useMemo, useState } from "react";
import { useCSVDownloader } from "react-papaparse";
import { useLocation, useNavigate } from "react-router-dom";
import { formattedDate, cleanString, formattedISODate } from "../../../utils";
import {
  reportSummaryApiToPendingReviewResponse,
  useMisconductReportSummaryListQuery,
} from "../misconductListQueries";
import AmendIcon from "../../../assets/amendment.svg";
import {
  computeFlagsCountTotal,
  MappedCSVReport,
  ReportsPendingReviewItem,
  ReportsPendingReviewResponse,
} from "../../../api/Reports";
import Pagination from "../../../shared-components/Pagination";
import { useAppContext } from "../../../AppProviders";
import { CacheKeys } from "../../../api/Cache";
import { LoadedSince } from "../../../shared-components/LoadedSince";
import ElipseIcon from "../../../shared-components/icons/ElipseIcon";
import TableReviewed from "../../TableReviewed";
import { PopMenu } from "../../../shared-components/PopMenu";
import PDFDownloadWrapper from "../../PDFDownloadWrapper";
import { usePermissionValidator } from "../../../api/UserPermissionValidators";
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ArrowUpTrayIcon,
  DocumentIcon,
  ExclamationCircleIcon as ExclamationIcon,
} from "@heroicons/react/24/outline";
import useNavigateToSummaryWithState from "../../useNavigateToSummaryWithState";
import { genericErrorLoadingMessage } from "../../utils";
import ReportActionSection from "../../ReportActionSection";
import LoadingSpinner from "../../../shared-components/LoadingSpinner";
import useRegeneratePDFWithToast from "../../PDFRegenerationWrapper";
import ReactTooltip from "react-tooltip";
import useNavigateNewPerson from "../../useNavigateNewPerson";
import ReportDeleteButton from "../../ReportDeleteButton";
import RecurCreateWrapper from "../../../recur/RecurCreateWrapper";
import {
  ReportEntry,
  ReportEntryStyle,
  ReportRowItem,
} from "../../helper-components/TableModels";
import {
  canOfferEmployeeScreeningSetup,
  renderReportMenuItem,
} from "../../renderReportMenuItem";

const pageSize = 15;

/**
 * Reviewed tab — ``GET /lists/misconduct/reviewed``. Legacy: ``../../ReportsReviewed.tsx``.
 */
export default function ReportsReviewedV2026() {
  const { CSVDownloader, Type } = useCSVDownloader();
  const { refreshCache, companiesFromAuth } = useAppContext();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const [filter, setSearch] = useState(searchParams.get("search") || "");
  const [page, setPage] = useState(1);
  const SearchName = searchParams.get("search");
  const SearchPage = searchParams.get("page");
  const {
    canViewAllReportsWithinCompany,
    canRerunReport,
    canRegeneratePDF,
    canDeleteReportReviewedTab,
    canDownloadPDF,
    person,
  } = usePermissionValidator();

  const reportReviewListInput = useMemo(() => {
    const company_uuid_list = companiesFromAuth?.allCompanies
      ? []
      : companiesFromAuth?.companyIds ?? [];
    return {
      individual: !canViewAllReportsWithinCompany,
      search: filter,
      page_size: pageSize,
      page,
      company_uuids: company_uuid_list,
    };
  }, [
    companiesFromAuth?.allCompanies,
    companiesFromAuth?.companyIds,
    canViewAllReportsWithinCompany,
    filter,
    page,
  ]);

  const listsReviewed = useMisconductReportSummaryListQuery({
    tab: "reviewed",
    individual: Boolean(reportReviewListInput.individual),
    names: reportReviewListInput.search ?? "",
    page: reportReviewListInput.page ?? 1,
    pageSize: reportReviewListInput.page_size ?? pageSize,
    enabled: true,
  });

  const loadingReports = listsReviewed.isLoading;
  const reportsError = listsReviewed.error;
  const reportsData = useMemo((): ReportsPendingReviewResponse | undefined => {
    if (!listsReviewed.data) return undefined;
    return reportSummaryApiToPendingReviewResponse(
      listsReviewed.data,
      reportReviewListInput.page,
      reportReviewListInput.page_size
    );
  }, [
    listsReviewed.data,
    reportReviewListInput.page,
    reportReviewListInput.page_size,
  ]);

  const getReportsFetchList = useCallback(() => {
    void listsReviewed.refetch();
  }, [listsReviewed]);

  const { regeneratePDF, errorGeneratePDF, loadingGeneratePDF } =
    useRegeneratePDFWithToast();
  const [recurReport, setRecurReport] =
    useState<ReportsPendingReviewItem | null>(null);
  const [isRecurOpen, setIsRecurOpen] = useState(false);

  const { navigateCreatePerson } = useNavigateNewPerson();
  const { navigateToSummary } = useNavigateToSummaryWithState();
  const navigate = useNavigate();

  const tableHeader = [
    "Name / Company",
    "Total Flags",
    { text: "Check Type / Behavior Kit", customClass: "w-48" },
    "Date Added",
    "Date Completed",
    "Client Reference ID",
    "Actions",
  ];
  if (canDeleteReportReviewedTab) tableHeader.push("DELETE");
  useEffect(() => {
    setSearch(cleanString(SearchName?.split(" ").join("%20") ?? ""));
    setPage(Number(1));
  }, [SearchName]);

  useEffect(() => {
    setPage(Number(SearchPage || "1"));
  }, [SearchPage]);
  const total = (): number => {
    return reportsData?.total_items ?? 0;
  };

  function handleReportClick(report: ReportsPendingReviewItem) {
    if (report.is_360) {
      navigate(`/360/${report.report_uuid}/summary`, {
        state: { originUrl: `${location.pathname}${location.search}` },
      });
    } else {
      navigateToSummary(report.report_uuid);
    }
  }

  function mapReportsToCsvData(
    reports: ReportsPendingReviewItem[] = []
  ): MappedCSVReport[] {
    return reports.map((report) => ({
      Name: report.full_name,
      Company: report.company_name,
      CommunityFlagCount: report.flags_count_payload?.community_count ?? 0,
      AdverseFlagCount: report.flags_count_payload?.adverse_flag_count ?? 0,
      KeywordCount: report.flags_count_payload?.keywords ?? 0,
      ArticleCount: report.flags_count_payload?.articles_count ?? 0,
      DateAdded: formattedISODate(report.created_at),
      DateCompleted: formattedISODate(report.finished_at),
      ReportURL: report.is_360
        ? `https://${window.location.hostname}/#/360/${report.report_uuid}/summary`
        : `https://${window.location.hostname}/#/reports/${report.report_uuid}/summary`,
    }));
  }

  function clearCache() {
    refreshCache(CacheKeys.reports);
    getReportsFetchList();
  }

  if (reportsError) {
    return (
      <div className="p-4 rounded-xl bg-red-300 text-red-900">
        {genericErrorLoadingMessage}
      </div>
    );
  }

  const popMenuReport = (report: ReportsPendingReviewItem) => {
    let _items = [];
    if (canDownloadPDF) {
      _items.push({
        component: (
          <PDFDownloadWrapper report_uuid={report.report_uuid}>
            {renderReportMenuItem(ArrowDownTrayIcon, "Download PDF", "text-sm")}
          </PDFDownloadWrapper>
        ),

        onClick: () => {},
      });
    }
    if (canRerunReport) {
      _items.push({
        component: renderReportMenuItem(
          ArrowPathIcon,
          "Rerun Person",
          "text-sm"
        ),
        onClick: () => {
          navigateCreatePerson({
            state: { rerun: true, parent_report: report },
          });
        },
      });
    }
    if (canRegeneratePDF) {
      _items.push({
        component: renderReportMenuItem(
          DocumentIcon,
          "PDF Regeneration",
          "text-sm"
        ),
        onClick: () => {
          regeneratePDF(report.report_uuid);
        },
      });
    }
    if (
      canOfferEmployeeScreeningSetup(person?.canCreateRecur, report.is_360)
    ) {
      _items.push({
        component: renderReportMenuItem(
          ArrowPathRoundedSquareIcon,
          "Setup Employee Rescreening",
          "text-sm"
        ),
        onClick: () => {
          setRecurReport(report);
          setIsRecurOpen(true);
        },
      });
    }
    return _items;
  };

  const convertDataToRows = (
    reportsData: ReportsPendingReviewResponse | undefined
  ): ReportRowItem<ReportsPendingReviewItem>[] => {
    if (!reportsData) return [];
    const reportRows: ReportRowItem<ReportsPendingReviewItem>[] =
      reportsData.items.map((report: ReportsPendingReviewItem) => {
        const data: ReportEntry[] = [
          {
            text: "",
            component: (
              <div>
                <div className="flex">
                  <div>
                    <span
                      onClick={() => handleReportClick(report)}
                      className="underline cursor-pointer font-semibold"
                    >
                      {report.full_name}
                    </span>
                    <small className="block font-normal">
                      {report.company_name}
                    </small>
                  </div>
                  {(report.workflow_payload?.rerun_from_parent_uuid ||
                    report.workflow_payload?.has_rerun) && (
                    <div>
                      <ArrowPathIcon className="w-5 ml-2 text-blue-600" />
                    </div>
                  )}
                  {report.was_amend && (
                    <img
                      className={"w-5 ml-2 text-blue-600"}
                      // @ts-ignore
                      src={AmendIcon}
                      alt={"amendment"}
                    />
                  )}
                </div>
              </div>
            ),
            style: ReportEntryStyle.COMPONENT,
          },

          {
            text: `${computeFlagsCountTotal(
              report.flags_count_payload
            ).toString()}`,
            style: ReportEntryStyle.TOOLTIP,
          },
          {
            component: (
              <div className="flex flex-col">
                <span className="border-b-2">{report.blueprint_name}</span>
                <small className="block">
                  {report.behavior_kit_name || "General"}
                </small>
              </div>
            ),
            text:
              report.blueprint_name +
              " " +
              (report.behavior_kit_name ?? "General"),
            style: ReportEntryStyle.COMPONENT,
            customClass: "w-48 truncate",
          },
          {
            text: report.created_at ? formattedDate(report.created_at) : "",
            style: ReportEntryStyle.COMPONENT,
            component: report.recur ? (
              <>
                <span>{formattedDate(report.created_at)}</span>
                <span data-tip="Employee Screening" className="cursor-pointer">
                  <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
                </span>
                <ReactTooltip effect="solid" />
              </>
            ) : (
              <>{formattedDate(report.created_at)}</>
            ),
          },
          {
            text: report.finished_at ? formattedDate(report.finished_at) : "",
            style: ReportEntryStyle.TEXT,
          },
          {
            text: report.client_report_id,
            style: ReportEntryStyle.TEXT,
          },
          {
            text: "",
            component: (
              <div className="pl-3 w-full">
                <PopMenu
                  button={<ElipseIcon />}
                  menuItems={popMenuReport(report)}
                />
              </div>
            ),
            customClass: "flex-1",
            style: ReportEntryStyle.COMPONENT,
          },
        ];
        if (canDeleteReportReviewedTab)
          data.push({
            text: "",
            component: (
              <ReportDeleteButton
                reportUUID={report.report_uuid}
                onDeletedSuccess={() => {
                  void getReportsFetchList();
                }}
              />
            ),
            style: ReportEntryStyle.COMPONENT,
          });
        return {
          report: report,
          report_uuid: report.report_uuid,
          isLocked: false,
          data: data,
        };
      });
    return reportRows;
  };
  return (
    <>
      <ReportActionSection>
        {loadingGeneratePDF && <LoadingSpinner />}
        {errorGeneratePDF && (
          <span title="Error PDF Regeneration">
            <ExclamationIcon className="w-6 pr-1 text-red-500" />
          </span>
        )}
        <LoadedSince
          className="w-44"
          reportData={{ timestamp: reportsData?.timestamp ?? Date.now() }}
          onClear={clearCache}
        />
        <CSVDownloader
          type={Type.Button}
          filename={`Fama Report List Page ${page} ${formattedISODate(
            new Date()
          )}`}
          bom={true}
          config={{
            delimiter: ",",
          }}
          data={mapReportsToCsvData(reportsData?.items)}
        >
          <ArrowUpTrayIcon
            data-tip={"Export all Reports in View"}
            className="w-6"
          ></ArrowUpTrayIcon>
          <ReactTooltip place="bottom" type="dark" effect="solid" />
        </CSVDownloader>
      </ReportActionSection>

      <TableReviewed
        isLoading={loadingReports}
        header={tableHeader}
        data={convertDataToRows(reportsData)}
      />
      {!loadingReports && (
        <Pagination
          pageSize={pageSize}
          currentPage={page}
          totalCount={total()}
          search={filter}
        />
      )}
      {recurReport && (
        <RecurCreateWrapper
          report={recurReport}
          isOpen={isRecurOpen}
          setModalOpen={setIsRecurOpen}
        />
      )}
    </>
  );
}
